import React from "react";
import { useTaxOrganizerStore } from "../../../../../utils/stores/taxOrganizerStore";
import {
  renderChoiceField,
  renderLongTextField,
  renderTextField,
} from "../FieldRenderers";
import DocsUpload from "../../Custom/UploadDocArea/DocsUpload";
import "./AdditionalQuestions.scss";
import { validateSingleQuestion } from "../../../../../utils/configs/additionalQuestionSchema";
import type { Question } from "../../../../../types";
import { OrganizerPages } from "@constants/TaxOrganizer.ts";

const AdditionalQuestions: React.FC = () => {
  const {
    updateAdditionalQuestionsResponses,
    validationErrors,
    additionalQuestionsResponses,
    additionalQuestionsConfig,
    setValidationErrors,
    currentPage,
    taxYear,
  } = useTaxOrganizerStore();

  const handleErrors = (name: string, value: string | string[] | null) => {
    // Validate only the changed question
    const error = validateSingleQuestion(
      value,
      additionalQuestionsConfig.questions.find(
        (question: Question) => question.id === name
      )
    );

    setValidationErrors((prevErrors: any) => ({
      ...prevErrors,
      additionalQuestions: {
        ...prevErrors.additionalQuestions,
        [name]: error,
      },
    }));
  };

  const handleFieldChange = async (
    name: string,
    value: string | string[] | null
  ) => {
    updateAdditionalQuestionsResponses({
      ...additionalQuestionsResponses,
      [name]: value,
    });
    handleErrors(name, value);
  };

  const renderQuestion = () => {
    return additionalQuestionsConfig?.questions?.map(
      (question: any, index: number) => {
        const fieldName = `${question?.id}`;
        const fieldValue = additionalQuestionsResponses?.[fieldName] ?? "";
        const errorMessage =
          validationErrors?.additionalQuestions?.[
            fieldName as keyof typeof validationErrors.additionalQuestions
          ] ?? "";
        const isDisabled = currentPage === OrganizerPages.REVIEW;
        const questionKey = question?.id || `question-${index}`;

        switch (question.type.name) {
          case "shortInput":
            return (
              <React.Fragment key={questionKey}>
                {renderTextField(
                  {
                    field: {
                      type: "textInput",
                      name: fieldName,
                      label: question.title,
                      description: question.description,
                      required: question?.isRequired,
                      placeholder: question?.placeholder,
                    },
                    value: fieldValue,
                    className: "additional-question-text-input",
                  },
                  {
                    groupIndex: 0,
                    handleFieldChange,
                    disabled: isDisabled,
                    errorMessage: errorMessage as string,
                  }
                )}
              </React.Fragment>
            );
          case "longInput":
            return (
              <React.Fragment key={questionKey}>
                {renderLongTextField(
                  {
                    type: "textarea",
                    name: fieldName,
                    label: question.title,
                    description: question.description,
                    required: question?.isRequired,
                    placeholder: question?.placeholder,
                  },
                  0,
                  fieldValue,
                  handleFieldChange,
                  isDisabled,
                  errorMessage as string,
                  "additional-question-long-text-input"
                )}
              </React.Fragment>
            );
          case "choice":
            return (
              <React.Fragment key={questionKey}>
                {renderChoiceField(
                  {
                    field: {
                      type: "choice",
                      name: fieldName,
                      label: question.title,
                      description: question.description,
                      options: question.type?.options,
                      multiselect: question.type?.isMultipleChoice,
                    },
                    value: fieldValue,
                  },
                  {
                    groupIndex: 0,
                    handleChange: handleFieldChange,
                    disabled: isDisabled,
                    errorMessage: errorMessage as string,
                    renderFields: () => {},
                  }
                )}
              </React.Fragment>
            );
          case "docUpload":
            return (
              <div key={questionKey} className="docUpload">
                <span className="docUpload-title">
                  <h2>{question?.title}</h2>
                  <p className="docUpload-title-required">
                    {question?.isRequired ? "*" : ""}
                  </p>
                </span>
                <p className="docUpload-description">{question?.description}</p>
                <DocsUpload
                  uploadTitleText={question?.id.toLowerCase()}
                  showUploadTitle={false}
                  taxYear={taxYear!}
                  isCustomQuestion={true}
                  onUploadComplete={() => {
                    handleFieldChange(question?.id, "uploaded");
                  }}
                  onRemove={() => {
                    handleFieldChange(question?.id, null);
                  }}
                />
              </div>
            );
          default:
            return <React.Fragment key={questionKey}>{null}</React.Fragment>;
        }
      }
    );
  };

  return (
    <div className="additional-questions">
      <h2 className="title">{additionalQuestionsConfig.title}</h2>
      <p className="description">{additionalQuestionsConfig.description}</p>
      <div className="questions">{renderQuestion()}</div>
    </div>
  );
};

export default AdditionalQuestions;
